import {Button, Drawer, Form, Space} from 'antd';
import React, {useEffect} from 'react';
import {useScaleBands, useCreateScaleBand, useUpdateScaleBand} from '../../../hooks/useScaleBands';
import {useTranslation} from '../../../hooks/useTranslation';
import ScaleBandForm from './ScaleBandForm';

interface ScaleBandDrawerProps {
  open: boolean;
  record: any | null;
  onClose: () => void;
}

const ScaleBandDrawer: React.FC<ScaleBandDrawerProps> = ({open, record, onClose}) => {
  const {t} = useTranslation();
  const [form] = Form.useForm();
  const {data: scaleBands = []} = useScaleBands();
  const createMutation = useCreateScaleBand();
  const updateMutation = useUpdateScaleBand();

  const isEdit = record != null;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        label: record.label || '',
        color: record.color || '',
        displayOrder: record.displayOrder ?? null,
        formula: record.formula || '',
      });
    } else {
      form.setFieldsValue({
        label: '',
        color: '',
        displayOrder: (scaleBands?.length || 0) + 1,
        formula: '',
      });
    }
  }, [open, record, scaleBands, form]);

  const handleSubmit = async () => {
    try {
      const row = await form.validateFields();
      const payload = {
        label: row.label,
        color: row.color,
        displayOrder: row.displayOrder ?? null,
        formula: row.formula ?? null,
      };
      if (isEdit) {
        await updateMutation.mutateAsync({id: record.id, data: payload});
      } else {
        const groupId = scaleBands[0]?.groupId;
        await createMutation.mutateAsync({...payload, ...(groupId ? {groupId} : {})});
      }
      onClose();
    } catch (err) {
      console.error('Validation Failed:', err);
    }
  };

  return (
    <Drawer
      title={
        isEdit
          ? t('settings.content.impactAssessment.formulaTable.actions.edit')
          : t('settings.content.impactAssessment.formulaTable.actions.add')
      }
      open={open}
      onClose={onClose}
      size="default"
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>
            {t('settings.content.impactAssessment.formulaTable.actions.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
          >
            {t('settings.content.impactAssessment.formulaTable.actions.save')}
          </Button>
        </Space>
      }
    >
      <ScaleBandForm form={form}/>
    </Drawer>
  );
};

export default ScaleBandDrawer;
